import React, { useState, useEffect } from 'react';

interface OfflineIndicatorProps {
    className?: string;
}

const OfflineIndicator: React.FC<OfflineIndicatorProps> = ({ className = '' }) => {
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [showOfflineToast, setShowOfflineToast] = useState(!navigator.onLine);
    const [showOnlineToast, setShowOnlineToast] = useState(false);

    useEffect(() => {
        const handleOnline = () => {
            setIsOnline(true);
            setShowOfflineToast(false);
            setShowOnlineToast(true);
            setTimeout(() => setShowOnlineToast(false), 3000);
        };

        const handleOffline = () => {
            setIsOnline(false);
            setShowOfflineToast(true);
        };

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    if (isOnline && !showOnlineToast) {
        return null;
    }

    return (
        <>
            {/* Offline Banner */}
            {!isOnline && showOfflineToast && (
                <div className={`offline-banner ${className}`}>
                    <div className="offline-content">
                        <div className="offline-icon">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M18.364 5.636l-12.728 12.728m0 0L12 12m-6.364 6.364L12 12m6.364-6.364L12 12"
                                />
                            </svg>
                        </div>
                        <div className="offline-text">
                            <span className="offline-title">You're offline</span>
                            <span className="offline-subtitle">
                                Some features may not be available. Please check your internet connection.
                            </span>
                        </div>
                        <button
                            onClick={() => setShowOfflineToast(false)}
                            className="offline-close"
                            aria-label="Dismiss offline notification"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                        </button>
                    </div>
                </div>
            )}

            {/* Back Online Toast */}
            {showOnlineToast && (
                <div className="online-toast">
                    <div className="online-toast-content">
                        <div className="online-icon">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                            </svg>
                        </div>
                        <span>Back online! Connection restored.</span>
                    </div>
                </div>
            )}

            <style>{`
        .offline-banner {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
          color: white;
          z-index: 1000;
          animation: slideDown 0.3s ease-out;
        }

        .offline-content {
          display: flex;
          align-items: center;
          padding: 12px 16px;
          max-width: 1200px;
          margin: 0 auto;
        }

        .offline-icon {
          margin-right: 12px;
          flex-shrink: 0;
        }

        .offline-text {
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        .offline-title {
          font-weight: 600;
          font-size: 14px;
          line-height: 1.2;
        }

        .offline-subtitle {
          font-size: 12px;
          opacity: 0.9;
          margin-top: 2px;
          line-height: 1.3;
        }

        .offline-close {
          background: none;
          border: none;
          color: white;
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
          transition: background-color 0.2s ease;
        }

        .offline-close:hover {
          background: rgba(255, 255, 255, 0.1);
        }

        .online-toast {
          position: fixed;
          bottom: 20px;
          right: 20px;
          background: linear-gradient(135deg, #059669 0%, #047857 100%);
          color: white;
          border-radius: 12px;
          padding: 16px;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
          z-index: 1000;
          animation: slideUp 0.3s ease-out, fadeOut 0.3s ease-out 2.7s forwards;
        }

        .online-toast-content {
          display: flex;
          align-items: center;
          font-size: 14px;
          font-weight: 500;
        }

        .online-icon {
          margin-right: 8px;
        }

        @keyframes slideDown {
          from {
            transform: translateY(-100%);
          }
          to {
            transform: translateY(0);
          }
        }

        @keyframes slideUp {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @keyframes fadeOut {
          from {
            opacity: 1;
          }
          to {
            opacity: 0;
            transform: translateY(20px);
          }
        }

        @media (max-width: 640px) {
          .offline-content {
            padding: 10px 12px;
          }

          .offline-title {
            font-size: 13px;
          }

          .offline-subtitle {
            font-size: 11px;
          }

          .online-toast {
            bottom: 10px;
            right: 10px;
            left: 10px;
            padding: 12px;
          }

          .online-toast-content {
            font-size: 13px;
          }
        }
  `}</style>
        </>
    );
};

export default OfflineIndicator;
